import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation, SUPPORTED_LANGUAGES } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  Bird, User, Mail, Lock, Loader2, ArrowRight,
  Shield, Globe, AlertCircle, Check, HardHat, Truck,
  LayoutDashboard, Eye, Wrench, PackageOpen
} from "lucide-react";

const ROLE_META = {
  driver:    { icon: Truck,          color: "bg-red-600 text-white",    label: "Driver" },
  machinist: { icon: HardHat,        color: "bg-amber-500 text-slate-950", label: "Machinist" },
  planner:   { icon: LayoutDashboard, color: "bg-lime-500 text-slate-950", label: "Planner" },
  service:   { icon: Wrench,         color: "bg-blue-500 text-white",   label: "Service" },
  transport: { icon: PackageOpen,    color: "bg-orange-500 text-white", label: "Transport" },
  viewer:    { icon: Eye,            color: "bg-slate-500 text-white",  label: "Viewer" },
};

export default function InviteJoinPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setAuth, setSession } = useAppContext();

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);

  const [lang, setLang] = useState(() => localStorage.getItem("pigeon-lang") || "en");
  const { t } = useTranslation(lang);

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState("lang"); // lang -> register

  useEffect(() => {
    if (!token) { setLoadError("No invite token provided"); setLoading(false); return; }
    api.getInviteInfo(token)
      .then((data) => {
        if (data.used) { setLoadError("This invite has already been used."); }
        else { setInvite(data); }
      })
      .catch(() => setLoadError("Invalid invite link."))
      .finally(() => setLoading(false));
  }, [token]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (!displayName.trim()) { setError("Please enter your name"); return; }
    if (!email.includes("@")) { setError("Please enter a valid email"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirmPw) { setError("Passwords do not match"); return; }

    setSubmitting(true);
    try {
      const res = await api.registerInvite({
        token, email: email.trim().toLowerCase(),
        display_name: displayName.trim(), password, language: lang,
      });

      // Save auth
      const authData = { jwt: res.jwt, user: res.user };
      setAuth(authData);
      localStorage.setItem("pigeon-auth", JSON.stringify(authData));
      localStorage.setItem("pigeon-lang", lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";

      // Set session to auto-enter hub
      setSession({
        hubId: res.hub_id,
        hubName: res.hub_name,
        role: res.role,
        displayName: res.display_name,
      });

      // Navigate based on hub status
      if (res.hub_status === "DEPLOYED") {
        nav(`/${res.role}`);
      } else {
        nav("/portal");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Registration failed. Try again.");
    }
    setSubmitting(false);
  };

  const roleMeta = ROLE_META[invite?.role] || ROLE_META.viewer;
  const RoleIcon = roleMeta.icon;

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-[100dvh] bg-white flex flex-col items-center justify-center p-6" data-testid="invite-error">
        <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Invalid Invite</h1>
        <p className="text-sm text-slate-500 mb-8">{loadError}</p>
        <button onClick={() => nav("/landing")} className="px-6 py-3 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200 transition-colors">
          Go to Homepage
        </button>
      </div>
    );
  }

  // Step 1: Language selection
  if (step === "lang") {
    return (
      <div className="min-h-[100dvh] bg-white flex flex-col items-center justify-center p-6" data-testid="invite-lang-step">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(220,38,38,0.03)_0%,transparent_70%)]" />
        <div className="relative z-10 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Bird className="w-8 h-8 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl tracking-tighter text-slate-900 mb-1">
            <span className="font-light">Join</span>{" "}
            <span className="font-black">{invite.hub_name}</span>
          </h1>
          <div className="flex items-center justify-center gap-2 mt-3 mb-8">
            <div className={`w-7 h-7 ${roleMeta.color} rounded-lg flex items-center justify-center`}>
              <RoleIcon className="w-4 h-4" />
            </div>
            <span className="text-sm font-bold text-slate-600 uppercase tracking-wide">{roleMeta.label}</span>
          </div>

          <p className="text-sm text-slate-400 font-medium mb-6">
            <Globe className="w-4 h-4 inline mr-1" />
            {t("choose_language") || "Choose your language"}
          </p>
          <div className="grid grid-cols-3 gap-2 mb-8">
            {SUPPORTED_LANGUAGES.map((l) => (
              <button key={l.code} data-testid={`invite-lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`p-3 rounded-xl text-center transition-all border-2 text-sm ${
                  lang === l.code
                    ? "bg-red-50 border-red-500 text-red-700 font-bold shadow-sm"
                    : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300 font-medium"
                }`}>
                <span className="block text-[10px] font-black opacity-40">{l.flag}</span>
                <span className="block text-xs">{l.label}</span>
              </button>
            ))}
          </div>
          <button data-testid="invite-lang-continue" onClick={() => setStep("register")}
            className="w-full py-4 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-black shadow-lg transition-all flex items-center justify-center gap-2">
            {t("continue_lang") || "Continue"} <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Registration form
  return (
    <div className="min-h-[100dvh] bg-white flex flex-col items-center justify-center p-6" data-testid="invite-register-step">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(220,38,38,0.03)_0%,transparent_70%)]" />
      <div className="relative z-10 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Bird className="w-7 h-7 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl tracking-tighter text-slate-900 mb-1">
            <span className="font-light">Join</span>{" "}
            <span className="font-black">{invite.hub_name}</span>
          </h1>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className={`w-6 h-6 ${roleMeta.color} rounded-lg flex items-center justify-center`}>
              <RoleIcon className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">{roleMeta.label}</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
              {t("your_name") || "Your Name"}
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="invite-name" type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t("name_placeholder") || "e.g. Jan De Vos"}
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-300 focus:border-red-400 focus:outline-none transition-colors" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
              {t("email") || "Email"}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-300 focus:border-red-400 focus:outline-none transition-colors" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
              {t("password") || "Password"}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="invite-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-300 focus:border-red-400 focus:outline-none transition-colors" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
              {t("confirm_password") || "Confirm Password"}
            </label>
            <div className="relative">
              <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input data-testid="invite-confirm-password" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                placeholder="Repeat password"
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-300 focus:border-red-400 focus:outline-none transition-colors" />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2" data-testid="invite-error-msg">
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
              <span className="text-xs text-red-600 font-medium">{error}</span>
            </div>
          )}

          <button data-testid="invite-submit" type="submit" disabled={submitting}
            className="w-full py-4 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-black shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-5 h-5" /> {t("create_account_join") || "Create Account & Join"}</>}
          </button>
        </form>

        {/* Already have account */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-400">
            {t("already_have_account") || "Already have an account?"}{" "}
            <button onClick={() => nav(`/auth?invite=${token}`)} className="text-red-600 font-bold hover:underline">
              {t("sign_in") || "Sign in"}
            </button>
          </p>
        </div>

        {/* Language badge */}
        <div className="mt-4 flex justify-center">
          <button onClick={() => setStep("lang")} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-[10px] font-bold text-slate-400 hover:border-slate-300 transition-colors">
            <Globe className="w-3 h-3" />
            {SUPPORTED_LANGUAGES.find((l) => l.code === lang)?.label || "English"}
          </button>
        </div>
      </div>
    </div>
  );
}
